/**
 * ChatPanel — the overlay chat window opened by the floating action button.
 *
 * Slides up from the bottom-right corner. Contains:
 * - Header with title, context badge, session history, close button
 * - Scrollable message area with streaming support
 * - Composer input at the bottom
 *
 * The panel auto-loads sessions on first open and scrolls to bottom on new messages.
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../auth/useAuth';
import { useCockpitContext } from '../../hooks/useCockpitContext';
import { useCockpitStore } from '../../store/CockpitStore';
import { useThemeClasses } from '../../providers/ThemeProvider';
import { cx } from '../../styles';
import { MessageBubble, StreamingMessage } from './MessageBubble';
import { ChatComposer } from './ChatComposer';

// ─── Panel component ─────────────────────────────────────────────────────────

export const ChatPanel: React.FC = () => {
  const { t } = useTranslation();
  const { token } = useAuth();
  const tc = useThemeClasses();
  const navigate = useNavigate();
  const cockpitContext = useCockpitContext();

  const {
    isOpen,
    close,
    messages,
    messagesLoading,
    isStreaming,
    streamingText,
    streamingReasoning,
    streamingToolCalls,
    activeSessionId,
    sessionContext,
    sessions,
    sessionsLoading,
    loadSessions,
    startNewSession,
    openSession,
    sendMessage,
    cancelStream,
  } = useCockpitStore();

  const [showSessionList, setShowSessionList] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const hasLoadedSessions = useRef(false);

  // Load sessions on first open
  useEffect(() => {
    if (isOpen && token && !hasLoadedSessions.current) {
      hasLoadedSessions.current = true;
      loadSessions(token);
    }
  }, [isOpen, token, loadSessions]);

  // Auto-scroll to bottom on new messages or streaming updates
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [messages.length, streamingText, streamingReasoning, isStreaming, streamingToolCalls]);

  // Handle send — pass live context so the store can lock it on first message
  const handleSend = useCallback(
    (text: string) => {
      if (!token) return;
      sendMessage(token, text, cockpitContext);
    },
    [token, cockpitContext, sendMessage],
  );

  // Handle new conversation — lock current context immediately
  const handleNewConversation = useCallback(() => {
    if (!token) return;
    startNewSession(token, cockpitContext);
    setShowSessionList(false);
  }, [token, startNewSession, cockpitContext]);

  // Handle open session from sidebar
  const handleOpenSession = useCallback(
    (sessionId: string) => {
      if (!token) return;
      openSession(token, sessionId);
      setShowSessionList(false);
    },
    [token, openSession],
  );
  // Display context: use locked session context, or live context if no session yet
  const displayContext = sessionContext ?? cockpitContext;
  // ── Context badge ──────────────────────────────────────────────────────────

  const scopeColors: Record<string, string> = {
    machine: 'bg-blue-500',
    line: 'bg-emerald-500',
    general: 'bg-gray-400',
  };

  const scopeLabels: Record<string, string> = {
    machine: t('cockpit.contextMachine', 'Machine'),
    line: t('cockpit.contextLine', 'Line'),
    general: t('cockpit.contextGeneral', 'All Data'),
  };

  return (
    <AnimatePresence>
      {isOpen && (
    <motion.div
      key="chat-panel"
      initial={{ opacity: 0, y: 30, scale: 0.92 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 30, scale: 0.92 }}
      transition={{
        type: 'spring',
        damping: 28,
        stiffness: 320,
        mass: 0.8,
      }}
      className={cx(
        'fixed z-50',
        // Mobile: full screen with small inset
        'inset-2 sm:inset-auto',
        // Desktop: positioned bottom-right
        'sm:bottom-24 sm:right-6',
        'sm:w-[400px] sm:max-w-[calc(100vw-48px)]',
        'sm:h-[600px] sm:max-h-[calc(100vh-120px)]',
        'rounded-2xl overflow-hidden',
        'shadow-2xl shadow-black/20',
        'border',
        tc.borderPrimary,
        tc.bgPrimary,
        'flex flex-col',
      )}
      style={{ transformOrigin: 'bottom right' }}
    >
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div
        className={cx(
          'flex items-center gap-2 px-4 py-3 border-b',
          tc.borderPrimary,
          'bg-gradient-to-r from-[#025a94] to-[#037ac7]',
        )}
      >
        {/* AI icon */}
        <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
          AI
        </div>

        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-white truncate">
            {t('cockpit.title', 'AI Assistant')}
          </h3>
          {/* Context badge */}
          <div className="flex items-center gap-1.5 mt-0.5">
            <span
              className={cx(
                'inline-flex items-center text-[10px] font-medium px-1.5 py-0.5 rounded-full text-white/90',
                scopeColors[displayContext.scope],
              )}
            >
              {scopeLabels[displayContext.scope]}
              {displayContext.label && `: ${displayContext.label}`}
            </span>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-0.5">
          {/* Session history toggle */}
          <button
            onClick={() => setShowSessionList(!showSessionList)}
            className="p-1.5 rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition-colors"
            title={t('cockpit.sessions', 'Conversations')}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
              <path fillRule="evenodd" d="M2 3.75A.75.75 0 012.75 3h14.5a.75.75 0 010 1.5H2.75A.75.75 0 012 3.75zm0 4.167a.75.75 0 01.75-.75h14.5a.75.75 0 010 1.5H2.75a.75.75 0 01-.75-.75zm0 4.166a.75.75 0 01.75-.75h14.5a.75.75 0 010 1.5H2.75a.75.75 0 01-.75-.75zm0 4.167a.75.75 0 01.75-.75h14.5a.75.75 0 010 1.5H2.75a.75.75 0 01-.75-.75z" clipRule="evenodd" />
            </svg>
          </button>

          {/* Open full sessions page */}
          <button
            onClick={() => {
              close();
              navigate('/cockpit/sessions');
            }}
            className="p-1.5 rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition-colors"
            title={t('cockpit.openFullPage', 'Open conversations page')}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
              <path fillRule="evenodd" d="M4.25 5.5a.75.75 0 00-.75.75v8.5c0 .414.336.75.75.75h8.5a.75.75 0 00.75-.75v-4a.75.75 0 011.5 0v4A2.25 2.25 0 0112.75 17h-8.5A2.25 2.25 0 012 14.75v-8.5A2.25 2.25 0 014.25 4h5a.75.75 0 010 1.5h-5z" clipRule="evenodd" />
              <path fillRule="evenodd" d="M6.194 12.753a.75.75 0 001.06.053L16.5 4.44v2.81a.75.75 0 001.5 0v-4.5a.75.75 0 00-.75-.75h-4.5a.75.75 0 000 1.5h2.553l-9.056 8.194a.75.75 0 00-.053 1.06z" clipRule="evenodd" />
            </svg>
          </button>

          {/* Close */}
          <button
            onClick={close}
            className="p-1.5 rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition-colors"
            title={t('cockpit.close', 'Close')}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
              <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
            </svg>
          </button>
        </div>
      </div>

      {/* ── Session list sidebar (slide overlay) ────────────────────────── */}
      <AnimatePresence>
        {showSessionList && (
          <SessionListOverlay
            sessions={sessions}
            sessionsLoading={sessionsLoading}
            activeSessionId={activeSessionId}
            onSelect={handleOpenSession}
            onNew={handleNewConversation}
            onClose={() => setShowSessionList(false)}
          />
        )}
      </AnimatePresence>

      {/* ── Messages area ───────────────────────────────────────────────── */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto py-4">
        {/* Loading state */}
        {messagesLoading && (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full" />
          </div>
        )}

        {/* Empty state */}
        {!messagesLoading && messages.length === 0 && !isStreaming && (
          <EmptyState onSuggestionClick={handleSend} />
        )}

        {/* Messages */}
        {messages.map((msg) => (
          <MessageBubble key={msg.id} message={msg} />
        ))}

        {/* Streaming message */}
        {isStreaming && (
          <StreamingMessage
            text={streamingText}
            reasoning={streamingReasoning}
            currentTool={streamingToolCalls.length > 0 ? streamingToolCalls[streamingToolCalls.length - 1] : null}
          />
        )}
      </div>

      {/* ── Composer ────────────────────────────────────────────────────── */}
      <ChatComposer
        onSend={handleSend}
        onCancel={cancelStream}
        isStreaming={isStreaming}
        disabled={!token}
      />
    </motion.div>
      )}
    </AnimatePresence>
  );
};

// ─── Session list overlay ────────────────────────────────────────────────────

const SessionListOverlay: React.FC<{
  sessions: ReturnType<typeof useCockpitStore.getState>['sessions'];
  sessionsLoading: boolean;
  activeSessionId: string | null;
  onSelect: (id: string) => void;
  onNew: () => void;
  onClose: () => void;
}> = ({ sessions, sessionsLoading, activeSessionId, onSelect, onNew, onClose }) => {
  const { t } = useTranslation();
  const tc = useThemeClasses();

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ type: 'spring', damping: 26, stiffness: 300 }}
      className={cx('absolute inset-0 z-10 flex flex-col', tc.bgPrimary)}
    >
      {/* Header */}
      <div className={cx('flex items-center justify-between px-4 py-3 border-b', tc.borderPrimary, 'bg-gradient-to-r from-[#025a94] to-[#037ac7]')}>
        <h3 className="text-sm font-semibold text-white">
          {t('cockpit.sessions', 'Conversations')}
        </h3>
        <div className="flex items-center gap-1">
          <button
            onClick={onNew}
            className="p-1.5 rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition-colors"
            title={t('cockpit.newConversation', 'New conversation')}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
              <path d="M10.75 4.75a.75.75 0 00-1.5 0v4.5h-4.5a.75.75 0 000 1.5h4.5v4.5a.75.75 0 001.5 0v-4.5h4.5a.75.75 0 000-1.5h-4.5v-4.5z" />
            </svg>
          </button>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
              <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
            </svg>
          </button>
        </div>
      </div>

      {/* Session list */}
      <div className="flex-1 overflow-y-auto py-2">
        {sessionsLoading && (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full" />
          </div>
        )}

        {!sessionsLoading && sessions.length === 0 && (
          <div className={cx('text-center py-8 text-sm', tc.textMuted)}>
            {t('cockpit.noSessions', 'No conversations yet')}
          </div>
        )}

        {sessions.map((session) => (
          <button
            key={session.id}
            onClick={() => onSelect(session.id)}
            className={cx(
              'w-full text-left flex items-center gap-3 px-4 py-3 transition-colors',
              tc.hover,
              session.id === activeSessionId && (tc.bgSecondary),
            )}
          >
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500/20 to-cyan-400/20 flex items-center justify-center flex-shrink-0">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" className={cx('w-3.5 h-3.5', tc.textMuted)}>
                <path d="M1 8.74c0 .983.713 1.825 1.69 1.943.904.108 1.817.19 2.737.243.363.02.688.231.85.556l1.052 2.103a.75.75 0 001.342 0l1.052-2.103c.162-.325.487-.535.85-.556.92-.053 1.833-.134 2.738-.243C14.287 10.565 15 9.723 15 8.74V4.26c0-.983-.713-1.825-1.69-1.943a44.89 44.89 0 00-10.62 0C1.713 2.435 1 3.277 1 4.26v4.482z" />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <div className={cx('text-sm font-medium truncate', tc.textPrimary)}>
                {session.title || t('cockpit.newConversation', 'New conversation')}
              </div>
              <div className={cx('text-xs mt-0.5', tc.textMuted)}>
                {formatRelativeTime(session.updated_at)}
              </div>
            </div>
          </button>
        ))}
      </div>
    </motion.div>
  );
};

// ─── Empty state ─────────────────────────────────────────────────────────────

const EmptyState: React.FC<{ onSuggestionClick: (text: string) => void }> = ({
  onSuggestionClick,
}) => {
  const { t } = useTranslation();
  const tc = useThemeClasses();

  const suggestions = [
    t('cockpit.suggestion1', 'How many machines had errors today?'),
    t('cockpit.suggestion2', 'What is the efficiency trend this week?'),
    t('cockpit.suggestion3', 'Which line has the most downtime?'),
  ];

  return (
    <div className="flex flex-col items-center px-6 py-8 text-center">
      <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center text-white text-lg font-bold shadow-lg mb-4">
        AI
      </div>
      <h3 className={cx('text-sm font-semibold mb-1', tc.textPrimary)}>
        {t('cockpit.title', 'AI Assistant')}
      </h3>
      <p className={cx('text-xs mb-5', tc.textMuted)}>
        {t('cockpit.emptyDescription', 'Ask questions about your production data')}
      </p>
      <div className="flex flex-col gap-2 w-full">
        {suggestions.map((text) => (
          <button
            key={text}
            onClick={() => onSuggestionClick(text)}
            className={cx(
              'text-left text-xs rounded-xl border px-3 py-2.5 transition-all',
              tc.bgSurface,
              tc.borderPrimary,
              tc.textSecondary,
              tc.hover,
              'hover:shadow-sm',
            )}
          >
            {text}
          </button>
        ))}
      </div>
    </div>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatRelativeTime(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHrs = Math.floor(diffMins / 60);
  if (diffHrs < 24) return `${diffHrs}h ago`;
  const diffDays = Math.floor(diffHrs / 24);
  if (diffDays < 7) return `${diffDays}d ago`;

  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
}

export default ChatPanel;
